import React, { useEffect, useState } from "react";
import ImageUpload from "./ImageUpload";
import { ReactComponent as FileIcon } from "../../../../assets/upload-file.svg";
import { uploadFile } from "../../../../utils/upload";
import httpServices from "../../../../services/httpServices";
import { useUser } from "../../../../context/UserContext";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../../component/common/overlay";

function VisualDetails({ setActiveStep, venueId, venueData }) {
  const { user } = useUser();
  const [floorPlanFiles, setFloorPlanFiles] = useState([]);
  const [isNewUpload, setIsNewUpload] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [venueImages, setVenueImages] = useState([]);
  const [floorPlanImages, setFloorPlanImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Convert URL to file
  const convertUrlToFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob(); // Fetch the image as a Blob
      return new File([blob], fileName, { type: blob.type }); // Convert the Blob to a File
    } catch (error) {
      console.error("Error converting URL to file:", error);
    }
  };

  // Update venue images by fetching the images and converting them into files
  const updateVenueImages = async () => {
    try {
      const imageFiles = await Promise.all(
        venueData?.venueSnapShot?.map(async (img) => {
          const fileName = img.split("/").pop(); // Extract file name from the URL
          const file = await convertUrlToFile(
            `https://zv.shellcode.cloud/upload/${img}`,
            fileName
          );
          return {
            image: file, // Store as image for consistency
            id: (Math.random() * 100).toFixed(0),
          };
        })
      );
      setVenueImages(imageFiles); // Update state with fetched images
    } catch (error) {
      console.error("Error updating venue images:", error);
    }
  };
  const updateFloorPlanImages = async () => {
    try {
      const floorPlanImageFiles = await Promise.all(
        venueData?.venueFloorPlanDesign?.map(async (img) => {
          const fileName = img.split("/").pop();
          const file = await convertUrlToFile(
            `https://zv.shellcode.cloud/upload/${img}`,
            fileName
          );
          return {
            image: file,
            id: (Math.random() * 100).toFixed(0),
          };
        })
      );
      setFloorPlanImages(floorPlanImageFiles); // Set the floor plan images
    } catch (error) {
      console.error("Error updating floor plan images:", error);
    }
  };

  useEffect(() => {
    if (venueData?.venueFloorPalnPdf?.length > 0) {
      setFloorPlanFiles([
        {
          name: venueData?.venueFloorPalnPdf?.[0],
          file: new File([], venueData?.venueFloorPalnPdf?.[0]),
        },
      ]);
      setPdfUrl(
        `https://zv.shellcode.cloud/upload/${venueData?.venueFloorPalnPdf?.[0]}`
      );
      updateVenueImages();
      updateFloorPlanImages();
    }
  }, [venueData]);

  // Handle image uploads for venue images and floor plan images
  const handleImageUpload = (setter) => (e) => {
    const files = Array.from(e.target.files);
    const newImages = files?.map((file) => ({
      image: file,
      id: (Math.random() * 100).toFixed(0),
    }));
    setter((prevImages) => [...prevImages, ...newImages]);
  };

  // Handle file upload for PDFs
  const handleFileUpload = (setter) => (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setPdfUrl(URL.createObjectURL(files[0]));
    }
    const newFiles = files.map((file) => ({
      file,
      id: (Math.random() * 100).toFixed(0),
      name: file.name,
    }));
    setter(newFiles);
    setIsNewUpload(true);
  };

  // View PDF in a new tab
  const handleViewPdf = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  // Delete image from state
  const handleDeleteImage = (setter) => (id) => {
    setter((prevImages) => prevImages.filter((image) => image.id !== id));
  };

  const isExistingImage = (id) => {
    console.log("id", id);
    const regex = /^\d{13}-.+/; // Matches a timestamp (13 digits) followed by a hyphen and any characters
    return regex.test(id);
  };
  // Submit form data, including file uploads
  const handleSubmit = async () => {
    if (venueData === null) {
      toast.warn("Please complete the previous step");
      return;
    }
    if (
      !venueImages.length ||
      !floorPlanFiles.length ||
      !floorPlanImages.length
    ) {
      toast.error("Please upload all the files and images");
      return;
    }
    setUploading(true);

    try {
      let venueFileNames = [...(venueData?.venueSnapShot || [])];
      let floorPlanFileNames = [...(venueData?.venueFloorPlanDesign || [])];
      let pdfFileNames = venueData?.venueFloorPalnPdf || [];

      // Filter out new images for venue snapshots
      const newVenueImages = venueImages.filter(
        (imgObj) => !isExistingImage(imgObj?.image?.name)
      );
      const existingVenueImages = venueImages.filter((imgObj) =>
        isExistingImage(imgObj?.image?.name)
      );

      if (newVenueImages.length > 0) {
        const venueFiles = newVenueImages?.map((imgObj) => imgObj.image);
        const venueUploadResponse = await uploadFile(venueFiles);
        const newVenueFileNames = venueUploadResponse.files.map(
          (file) => file.filename
        );
        venueFileNames = [
          ...existingVenueImages.map((img) => img.image.name),
          ...newVenueFileNames,
        ];
      }

      // Filter out new images for floor plan designs
      const newFloorPlanImages = floorPlanImages.filter(
        (imgObj) => !isExistingImage(imgObj?.image?.name)
      );
      const existingFloorPlanImages = floorPlanImages.filter((imgObj) =>
        isExistingImage(imgObj?.image?.name)
      );

      // Upload only new floor plan images
      if (newFloorPlanImages.length > 0) {
        const floorPlanFilesUpload = newFloorPlanImages.map(
          (imgObj) => imgObj.image
        );
        const floorPlanUploadResponse = await uploadFile(floorPlanFilesUpload);
        const newFloorPlanFileNames = floorPlanUploadResponse.files.map(
          (file) => file.filename
        );
        floorPlanFileNames = [
          ...existingFloorPlanImages.map((img) => img.image.name),
          ...newFloorPlanFileNames,
        ];
      }

      // Upload PDF if a new PDF is uploaded
      if (floorPlanFiles.length > 0 && isNewUpload) {
        const formData = new FormData();
        formData.append("file", floorPlanFiles[0].file);
        const pdfUploadResponse = await uploadFile(formData);
        pdfFileNames = pdfUploadResponse.files.map((file) => file.filename);
      }

      // Update venue with uploaded files or existing data
      const { response, error } = await httpServices.put(
        `/venue/update/${venueId}`,
        {
          stepsDone: 2,
          venueSnapShot: venueFileNames, // Send both new and old venue images
          venueFloorPalnPdf: pdfFileNames, // Send new or existing PDF file
          venueFloorPlanDesign: floorPlanFileNames, // Send both new and old floor plan images
          userId: user?.userId,
        }
      );

      if (error) throw error;

      if (response.status === 200) {
        toast.success("Visual details saved successfully");
        setActiveStep(2);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-3 p-3 bg-white relative ">
      {uploading && <LoadingOverlay text={"Uploading..."} />}
      <h3 className="text-blue fw-semibold text-xl mb-4">Visual Details</h3>

      {/* Floor Plan PDF */}
      <section>
        <h4 className="text-[#24252D] font-medium text-base mt-[20px] mb-2">
          Floor Plan PDF
        </h4>
        <div
          className="h-[70px] w-[16rem] bg-[#F5F6FA] relative border-2 border-gray-300 border-dashed rounded-lg flex items-center justify-center cursor-pointer"
          id="dropzone"
        >
          <input
            type="file"
            accept=".pdf"
            className="absolute w-full h-full opacity-0 z-50"
            onChange={handleFileUpload(setFloorPlanFiles)}
          />
          <div className="text-center flex items-center justify-center gap-3 cursor-pointer">
            <FileIcon />
            <span className="text-[#515B6B] font-medium">Upload File</span>
          </div>
        </div>
        {floorPlanFiles.length > 0 && (
          <div className="mt-2">
            <span className="text-sm text-[#245199] font-medium">
              Selected file: {floorPlanFiles?.[0]?.name}
            </span>
            <button
              className="ml-4 text-sm text-blue-600 hover:underline cursor-pointer"
              onClick={handleViewPdf}
            >
              View
            </button>
          </div>
        )}
      </section>

      {/* Venue Snapshots */}
      <ImageUpload
        images={venueImages}
        handleImageUpload={handleImageUpload(setVenueImages)}
        handleDeleteImage={handleDeleteImage(setVenueImages)}
        title="Venue Snapshots"
      />

      {/* Floor plan design */}
      <ImageUpload
        images={floorPlanImages}
        handleImageUpload={handleImageUpload(setFloorPlanImages)}
        handleDeleteImage={handleDeleteImage(setFloorPlanImages)}
        title="Floor plan design"
      />

      <div className="h-48 flex justify-center items-end">
        <button
          type="submit"
          className="gradian-btn text-white px-3 py-2"
          onClick={handleSubmit}
          disabled={uploading}
        >
          Save & Proceed
        </button>
      </div>
    </div>
  );
}

export default VisualDetails;
